<script setup lang="ts">
import { api } from '@/api';
import type { RoleCatalogDto, UserCatalogDto } from '@/api/generator';
import { ref } from 'vue';
import { computed } from 'vue';
import { onMounted } from 'vue';
import { type SelectItemTypeObject } from '@/components/forms/types';
import VeeAutocomplete from '@/components/forms/Autocomplete/VeeAutocomplete.vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { I18nKey } from '@/utils/typings';
import { useTranslation } from 'i18next-vue';
import { useFieldValue } from 'vee-validate';

const { t } = useTranslation();

const roles = ref<RoleCatalogDto[]>();
const users = ref<UserCatalogDto[]>();

const assignedRoles = useFieldValue<string[]>('assignedRoles');
const assignedUsers = useFieldValue<string[]>('assignedUsers');

async function loadRoles() {
  const { result: usersResult } = await api.usersApi.getUsersCatalog();
  const { result: rolesResult } = await api.rolesApi.getRolesCatalog();
  if (usersResult && rolesResult) {
    roles.value = rolesResult;
    users.value = usersResult;
  }
}

const rolesPlaceholder = computed(() => (!assignedUsers.value?.length ? t('Required') : ''));
const usersPlaceholder = computed(() => (!assignedRoles.value?.length ? t('Required') : ''));

const rolesOptions = computed(() =>
  roles.value?.map<SelectItemTypeObject<RoleCatalogDto>>((role) => ({
    title: role.name,
    value: role,
  })),
);

const userOptions = computed(() =>
  users.value?.map<SelectItemTypeObject<UserCatalogDto>>((user) => ({
    title: user.lastName,
    value: user,
  })),
);

onMounted(loadRoles);
</script>

<template>
  <div>
    <VeeAutocomplete
      :items="rolesOptions"
      solo
      chips
      clearable
      multiple
      :label="$t('AssignedRoles')"
      name="assignedRoles"
      :placeholder="rolesPlaceholder"
    >
      <template #itemTitle="{ value: role }">
        <IdentifierLabel
          :label="role.displayName ? $t(role.displayName as I18nKey) : role.name"
          :identifier="role.iuid"
        />
      </template>
    </VeeAutocomplete>

    <VeeAutocomplete
      :items="userOptions"
      class="gutterTop"
      solo
      chips
      clearable
      multiple
      :label="$t('AssignedToUser')"
      name="assignedUsers"
      :placeholder="usersPlaceholder"
    >
      <template #itemTitle="item">
        <IdentifierLabel :label="item.value.fullName" :identifier="item.value.iuid" />
      </template>
    </VeeAutocomplete>
  </div>
</template>
